import firestore from "src/apiService/firestore";
import { getUserId } from "src/config/storage";

/**
 * Existence check for platform ids. Queries `user` + idField (`in` chunks of
 * 10), then optionally filters `upload` / `import_platform` in memory.
 *
 * - With `uploadIds`: session resume (direct/initial) — only docs from this
 *   import's Uploads count.
 * - Without `uploadIds`: daily catch-up — any matching sale counts (Option A).
 */
export async function existingIdsByField(args: {
  collection: "Sales" | "Inventory" | "Transactions";
  idField: "transaction_id" | "listing_id";
  ids: string[];
  uploadIds?: string[];
  importPlatform?: string;
}): Promise<Set<string>> {
  const found = new Set<string>();
  if (!args.ids.length) return found;

  const uid = await getUserId();
  const db = firestore();
  const uploadSet =
    args.uploadIds != null ? new Set(args.uploadIds.filter(Boolean)) : null;
  if (uploadSet && !uploadSet.size) return found;

  // Firestore `in` supports max 10; chunk platform id lookups then filter.
  const chunkSize = 10;
  for (let i = 0; i < args.ids.length; i += chunkSize) {
    const chunk = args.ids.slice(i, i + chunkSize);
    const snap = await db
      .collection(args.collection)
      .where("user", "==", uid)
      .where(args.idField, "in", chunk)
      .get();
    for (const doc of snap.docs) {
      const data = doc.data();
      if (uploadSet) {
        const upload = data.upload as string | undefined;
        if (!upload || !uploadSet.has(upload)) continue;
      }
      if (
        args.importPlatform != null &&
        data.import_platform !== args.importPlatform
      ) {
        continue;
      }
      const id = data[args.idField];
      if (id != null) found.add(String(id));
    }
  }
  return found;
}

export function isLeaseExpired(
  runningTs: number | undefined | null,
  now: number,
  ttlMs: number
): boolean {
  if (runningTs == null || runningTs === 0) return true;
  return !(runningTs && runningTs + ttlMs > now);
}

/** Map platform ids → boolean flags parallel to the input list. */
export function flagsForIds(
  ids: Array<string | null>,
  found: Set<string>
): boolean[] {
  return ids.map((id) => (id ? found.has(id) : false));
}

/**
 * True when any id in the group is in `found` (alias-aware existence).
 * Empty groups → false.
 */
export function flagsForIdGroups(
  idGroups: string[][],
  found: Set<string>
): boolean[] {
  return idGroups.map((ids) => ids.some((id) => found.has(id)));
}
